import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame } from '../components/Frame';
import { KrabenMark } from '../components/KrabenMark';
import { ANCHORS, type Anchor } from '../data/crew';
import { loadProfile, saveProfile } from '../lib/state';
import { writeUserDoc } from '../lib/feed';
import { reminderAt, timeLabel, icsUrl, googleCalendarUrl } from '../lib/calendar';
import { track } from '../analytics';
import { useViewed } from '../lib/viewed';

/**
 * R-02, your moment: asked once, after the first card. A self-chosen anchor
 * or a self-chosen time are equally good (Keller 2021); the reminder later
 * repeats the plan back in the person's words. One question only.
 *
 * Then, once the moment is theirs, the offer to put tomorrow in the calendar
 * they already keep (owner, 2026-09-18). It is one tap, nothing is sent
 * anywhere, and "No thanks" is the same size as yes.
 *
 * Every way out of here lands on the road (owner, 2026-09-18): this screen sits
 * between the reward and the road, and dropping someone on the feed instead was
 * the reason Day 1 seemed to end somewhere nobody chose.
 */
export function Moment() {
  useViewed('moment');
  const nav = useNavigate();
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const [time, setTime] = useState('09:00');
  const [asked, setAsked] = useState(false);
  const [added, setAdded] = useState(false);
  const at = reminderAt(anchor || undefined, anchor === 'time' ? time : undefined);

  const keep = async () => {
    const p = { ...loadProfile(), anchor: anchor || undefined, anchorTime: anchor === 'time' ? time : undefined };
    saveProfile(p);
    track('anchor_set', { anchor, time: anchor === 'time' ? time : undefined });
    const tz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return undefined; } })();
    writeUserDoc({ anchor: anchor ? { kind: anchor, time: anchor === 'time' ? time : undefined } : undefined, tz }).catch(() => {});
    setAsked(true);
  };

  // Both calendars are offered at once, and neither can report back. The old
  // screen claimed "Sent to your calendar" the instant the button was pressed,
  // which on an iPhone was a lie (interview 2026-09-18): nothing had happened
  // and the only working link was hidden behind that claim.
  const added2 = (how: 'apple' | 'google') => { setAdded(true); track('calendar_reminder', { how, anchor }); };

  if (asked) {
    const label = ANCHORS.find((a) => a.id === anchor)?.label.toLowerCase();
    return (
      <Frame left={<span className="tag">Day 1</span>}>
        <div className="row between"><h1>Shall I put tomorrow in your calendar?</h1><KrabenMark size={72} /></div>
        <p className="why">One reminder, tomorrow at {timeLabel(at)}{anchor && anchor !== 'time' ? ` — ${label}` : ''}. It goes in your own calendar; nothing is sent anywhere.</p>
        <div className="stack" style={{ marginTop: 'auto' }}>
          <a className="btn" href={icsUrl(at)} onClick={() => added2('apple')}>Add to Apple Calendar</a>
          <a className="btn butter" href={googleCalendarUrl(at)} target="_blank" rel="noreferrer" onClick={() => added2('google')}>Add to Google Calendar</a>
          {added && <p className="hint" style={{ textAlign: 'center' }}>If your calendar did not open, the other button adds the same reminder.</p>}
          <button className={added ? 'btn ghost' : 'link'} onClick={() => { if (!added) track('calendar_skipped', { anchor }); nav('/path'); }}>{added ? 'Done' : 'No thanks'}</button>
        </div>
      </Frame>
    );
  }

  return (
    <Frame left={<span className="tag">Day 1</span>}>
      <div className="row between"><h1>When does an exercise fit in your day?</h1><KrabenMark size={72} /></div>
      <p className="why">Pick a time and tomorrow’s exercise will be ready then. Save your work with your email, and I’ll send you a reminder too.</p>
      <div className="chips">
        {ANCHORS.map((a) => <button key={a.id} className={`chip ${anchor === a.id ? 'on' : ''}`} onClick={() => setAnchor(a.id)}>{a.label}</button>)}
      </div>
      {anchor === 'time' && <input className="input" type="time" value={time} onChange={(e) => setTime(e.target.value)} style={{ maxWidth: 160 }} />}
      <div className="stack" style={{ marginTop: 'auto' }}>
        <button className="btn" onClick={keep} disabled={!anchor}>Set my time</button>
        <button className="link" onClick={() => { track('anchor_skipped'); nav('/path'); }}>Skip</button>
      </div>
    </Frame>
  );
}
